/**
 * 门店营收监控页面（Store Revenue Monitor）
 *
 * 功能概述：
 * - 顶部统计卡片：展示所有酒店的汇总维度数据（与门店列表数据源分离，避免联动重渲染）
 * - 搜索面板：大区/省/市/品牌/门店/状态等筛选；关键词在点击「搜索」后生效（输入不自动请求）
 * - 门店列表：按筛选条件懒加载酒店，每个门店独立加载数据、独立刷新，支持可见性检测与定时自动刷新
 * - 右侧 AI 面板：点击统计卡片/门店标题/图表组件标题时，将上下文发送给 CompsAi 进行分析
 *
 * 数据流与隔离：
 * - 统计卡片：useAutoMonitor → dimensionResults → StatisticCards（全局汇总，不依赖可见门店）
 * - 门店详情：useLoadingPage（列表） + StoreDataLoader → useLoadingStoreData（单店数据），与统计卡片完全隔离
 */

import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useDeferredValue,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Empty, message, Spin, Splitter } from "antd";
import type { FormInstance } from "antd";
import { ContainerPanel, CompsSearch } from "@ms/shared-components";
import { CompsAi } from "../../compsAi";
import type { CompsAiNewDesignRef } from "../../compsAi";
import type { ButtonConfig } from "@ms/shared-components/containers/ContainerPanel";
import "./index.css";

// ---------- 配置 ----------
import {
  createSearchItems,
  createSearchButtons,
  SEARCH_PANEL_CONFIG,
  type SearchButtonConfig,
} from "./configs/panelSearchConfig";
import { createThinkingModes } from "./configs/panelAnalysisConfig";

// ---------- 组件 ----------
import { HotelPlaceholder } from "./components";
import StatisticCards from "./components/StatisticCards";
import { StoreDataLoader } from "@ms/share";

// ---------- Hooks ----------
import {
  useAiAssistant,
  getComponentLabel,
  ALL_COMPONENT_LIST,
} from "./hooks/useAiAssistant";
import { useAreaOptions } from "../../hooks/useAreaOptions";
import { useBrandOptions } from "../../hooks/useBrandOptions";
import { useHotelOptions } from "../../hooks/useHotelOptions";
import { useLoadingPage } from "./hooks/useLoadingPage";
import { useAutoMonitor } from "./hooks/useAutoMonitor";
import { useStoreVisibilityManager } from "./hooks/useStoreVisibility";
import { useStoreRefreshTime } from "./hooks/useStoreRefreshTime";
import { useStoreRefreshRegistry } from "./hooks/useStoreRefreshRegistry";

// ---------- 工具 ----------
import { getIcon } from "./utils/iconUtils";
import { createLogger } from "./utils/logger";
import { convertStoreComponentClickToStandard } from "./utils/aiMessageConverter";
import { buildComponentDataPackage } from "./utils/buildComponentDataPackage";
import { getBusinessReportDailyWithCache } from "./utils/businessReportDailyCache";
import { buildOperationAnalysisFromList } from "./utils/operationAnalysisFromBusinessReport";
import { getRoomStatusRecentlyWithCache } from "./utils/roomStatusRecentlyCache";
import { normalizeToNumberArray } from "./utils/normalize";
import { shouldShowDebugFloat } from "../../utils/debugFloatEnabled";
import { useStoreRevenueMonitorLayoutDebugSampling } from "../../hooks/useStoreRevenueMonitorLayoutDebugSampling";
// 开发环境：在控制台通过 window.__storeRevenueCaches 查看房态/渠道价格/RevPAR/运营分析等内存缓存
import "./utils/storeRevenueCacheDebug";

const logger = createLogger("StoreRevenueMonitor");

/** 首屏列表只挂载并加载数据的门店行数；触底每次再增加 1 行 */
const INITIAL_VISIBLE_STORE_ROWS = 3;
/** 判定「滚到底」的像素容差 */
const LIST_SCROLL_BOTTOM_THRESHOLD_PX = 80;

/** 出租率等运营分析：后端 store-data-from-cache 返回空结构，需用前端 businessReport 补数据 */
const OPERATION_ANALYSIS_TYPES = [
  "occupancy-analysis",
  "room-health",
  "customer-source",
  "discount-analysis",
  "channel-info",
  "operation-efficiency",
] as const;

/** 截图模式：仅渲染单个门店的单个组件，供后端 /api/store-revenue/component-screenshot 无头浏览器截图 */
const ScreenshotModeView: React.FC<{
  hotelId: number;
  componentType: string;
  token: string;
  storeName: string;
  /** URL 传入的 dateRange，如 last7Days / last30Days，用于客源等组件的粒度 */
  dateRange?: string;
  /** URL 传入的 orderTrafficGranularity，如 小时 | 天，用于订单流速组件的粒度 */
  orderTrafficGranularity?: string;
}> = ({ hotelId, componentType, token, storeName, dateRange, orderTrafficGranularity }) => {
  if (typeof sessionStorage !== "undefined" && token) {
    sessionStorage.setItem("token", token);
  }
  return (
    <div
      style={{
        padding: 16,
        background: "#f5f5f5",
        minHeight: 400,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
      }}
    >
      <StoreDataLoader
        storeId={hotelId}
        storeName={storeName}
        refreshKey={0}
        focusComponentType={componentType}
        isScreenshotMode
        screenshotDateRange={dateRange}
        screenshotOrderTrafficGranularity={orderTrafficGranularity}
        onTitleClick={() => {}}
        onRefresh={() => {}}
        onComponentTitleClick={() => {}}
      />
    </div>
  );
};

const StoreRevenueMonitor: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const screenshotHotelIdRaw = searchParams.get("hotelId");
  const screenshotComponentType = searchParams.get("componentType");
  const screenshotToken = searchParams.get("token");
  const screenshotStoreName =
    searchParams.get("storeName") || (screenshotHotelIdRaw ? `酒店${screenshotHotelIdRaw}` : "");
  const screenshotDateRange = searchParams.get("dateRange") || undefined;
  const screenshotOrderTrafficGranularity = searchParams.get("orderTrafficGranularity") || undefined;
  const isScreenshotPath =
    location.pathname.endsWith("/store-revenue-monitor/screenshot") ||
    location.pathname.endsWith("/store-revenue-monitor/screenshot-v2") ||
    location.pathname.endsWith("/screenshot");
  const isScreenshotMode =
    (searchParams.get("screenshot") === "1" || isScreenshotPath) &&
    screenshotHotelIdRaw &&
    screenshotComponentType;
  const screenshotHotelId = isScreenshotMode ? Number(screenshotHotelIdRaw) : 0;

  if (isScreenshotMode && screenshotHotelId && screenshotComponentType) {
    return (
      <ScreenshotModeView
        hotelId={screenshotHotelId}
        componentType={screenshotComponentType}
        token={screenshotToken || ""}
        storeName={screenshotStoreName}
        dateRange={screenshotDateRange}
        orderTrafficGranularity={screenshotOrderTrafficGranularity}
      />
    );
  }
  return <StoreRevenueMonitorContent />;
};

/** 门店营收监控主内容（非截图模式），所有 hooks 在此组件内保证顺序稳定 */
const StoreRevenueMonitorContent: React.FC = () => {
  // ---------------------------------------------------------------------------
  // 搜索与表单
  // ---------------------------------------------------------------------------
  const {
    clearAll,
    handleStatCardClickFromComponent,
    handleStoreTitleClickFromComponent,
    handleComponentTitleClickFromComponent,
  } = useAiAssistant(undefined, undefined, { skipPageConfig: true });

  /** 右侧 CompsAi 组件 ref，用于非受控模式下调用 receiveMessage 发送分析请求 */
  const compsAiRef = useRef<CompsAiNewDesignRef | null>(null);
  /** 上次点击的组件上下文，用于判断是否需先清空 CompsAi 选择再展示新内容 */
  const lastComponentContextRef = useRef<{
    storeId: string | number;
    componentType: string;
  } | null>(null);
  /** 上次点击时传入的门店数据，供 AI 助手列表「xxx 分析」按组件类型打包发送 */
  const lastStoreDataRef = useRef<{
    storeId: number;
    storeName: string;
    storeData: any;
  } | null>(null);
  /** 当前在 AI 助手中显示的门店名称（点击门店标题或组件时更新） */
  const [currentStoreNameForAi, setCurrentStoreNameForAi] = useState<string>("");
  /** 当前在 AI 助手中关注的门店 ID（用于 CompsAi 门店 tab） */
  const [currentStoreIdForAi, setCurrentStoreIdForAi] = useState<string>("");
  /** 当前选中的门店 ID（点击门店标题时设置），用于列表分析时从缓存取该店数据 */
  const currentStoreIdForAiRef = useRef<string>("");
  /** 各门店数据就绪时写入，供「仅点过门店标题、未点组件」时列表分析使用 */
  const storeDataCacheRef = useRef<Record<string, { storeId: number; storeName: string; storeData: any }>>({});

  /** DBG 浮窗：布局高度采样（容器 / 页面壳 / 左右栏 / 列表 / AI） */
  const layoutDebugContainerRef = useRef<HTMLDivElement | null>(null);
  const layoutDebugLeftInnerRef = useRef<HTMLDivElement | null>(null);
  const layoutDebugStatRef = useRef<HTMLDivElement | null>(null);
  const layoutDebugSearchRef = useRef<HTMLDivElement | null>(null);
  const layoutDebugListHostRef = useRef<HTMLDivElement | null>(null);
  const layoutDebugAiWrapRef = useRef<HTMLDivElement | null>(null);

  /** 当前搜索表单值（用于地区/品牌等枚举联动与选项展示） */
  const [formValues, setFormValues] = useState<any>({});
  /** 已提交的筛选条件（仅点击「搜索」后更新，用于列表请求） */
  const [submittedFormValues, setSubmittedFormValues] = useState<any>({});
  /** 已提交的搜索关键词（点击「搜索」后更新，作为 useLoadingPage 的 searchKeyword） */
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] =
    useState<string>("");
  /** 地区联动时 setFieldsValue 会再次触发 onValuesChange，用此标志打断连锁更新，避免死循环 */
  const isUpdatingRef = useRef<boolean>(false);

  /** 搜索表单 ref；关键词仅在点击「搜索」时写入 debouncedSearchKeyword（供列表请求） */
  const searchFormRef = useRef<FormInstance | null>(null);
  const applySearchSubmit = useCallback((values: any) => {
    const normalizedValues = values || {};
    setFormValues(normalizedValues);
    setSubmittedFormValues(normalizedValues);
    setDebouncedSearchKeyword(
      normalizedValues?.search || normalizedValues?.hotelName || "",
    );
  }, []);
  const handleSearchClick = useCallback(() => {
    if (!searchFormRef.current) return;
    searchFormRef.current
      .validateFields()
      .then((values) => {
        applySearchSubmit(values);
      })
      .catch(() => {
        applySearchSubmit(searchFormRef.current?.getFieldsValue() || {});
      });
  }, [applySearchSubmit]);
  const handleReset = useCallback(() => {
    searchFormRef.current?.resetFields();
    setFormValues({});
    setSubmittedFormValues({});
    setDebouncedSearchKeyword("");
  }, []);

  /** 大区 / 省 / 市下拉选项（依赖 formValues 做联动） */
  const { regionOptions, provinceOptions, cityOptions } =
    useAreaOptions(formValues);
  /** 品牌下拉选项 */
  const { brandOptions } = useBrandOptions();

  /** 从当前表单中解析出的筛选 ID 数组，用于下拉联动与选项过滤展示 */
  const regionIds = normalizeToNumberArray(formValues?.regionId);
  /** 从已提交条件中解析出的筛选 ID 数组，用于列表请求 */
  const submittedRegionIds = normalizeToNumberArray(submittedFormValues?.regionId);
  const submittedProvinceIds = normalizeToNumberArray(submittedFormValues?.provinceId);
  const submittedCityIds = normalizeToNumberArray(submittedFormValues?.cityId);
  const submittedHotelIds = normalizeToNumberArray(submittedFormValues?.hotelId);
  const submittedBrandIds = normalizeToNumberArray(submittedFormValues?.brandIds);
  const submittedStatusArr = normalizeToNumberArray(submittedFormValues?.status);
  /** 门店状态：0=已上线, 1=已下线, 2=待上线；无选择时默认 0 */
  const submittedStatus = submittedStatusArr.length > 0 ? submittedStatusArr[0] : 0;
  /** 是否包含测试门店（如 hotelId=1），默认不显示 */
  const submittedShowTestStore = submittedFormValues?.showTestStore === true;
  /** 当前表单里的“包含测试门店”仅用于门店下拉展示 */
  const showTestStore = formValues?.showTestStore === true;

  // ---------------------------------------------------------------------------
  // 酒店列表加载（懒加载 + 筛选）
  // ---------------------------------------------------------------------------
  /** 列表有数据且布局就绪后再触发首屏批量加载（固定 3 店，不按视口放大） */
  const [listViewportReady, setListViewportReady] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(INITIAL_VISIBLE_STORE_ROWS);

  const listFilterKey = useMemo(
    () =>
      JSON.stringify({
        searchKeyword: debouncedSearchKeyword || "",
        regionIds: [...submittedRegionIds].sort((a, b) => a - b).join(","),
        provinceIds: [...submittedProvinceIds].sort((a, b) => a - b).join(","),
        cityIds: [...submittedCityIds].sort((a, b) => a - b).join(","),
        hotelIds: [...submittedHotelIds].sort((a, b) => a - b).join(","),
        brandIds: [...submittedBrandIds].sort((a, b) => a - b).join(","),
        status: submittedStatus,
        showTestStore: submittedShowTestStore,
      }),
    [
      debouncedSearchKeyword,
      submittedRegionIds,
      submittedProvinceIds,
      submittedCityIds,
      submittedHotelIds,
      submittedBrandIds,
      submittedStatus,
      submittedShowTestStore,
    ],
  );

  const {
    hotelListItems,
    loading: hotelsLoading,
    scrollContainerRef,
    loadStore,
    loadNextBatch,
    refreshStore,
    getStoreRefreshKey,
    totalCount,
    loadedCount,
  } = useLoadingPage({
    searchKeyword: debouncedSearchKeyword,
    regionIds: submittedRegionIds.length > 0 ? submittedRegionIds : undefined,
    provinceIds: submittedProvinceIds.length > 0 ? submittedProvinceIds : undefined,
    cityIds: submittedCityIds.length > 0 ? submittedCityIds : undefined,
    hotelIds: submittedHotelIds.length > 0 ? submittedHotelIds : undefined,
    brandIds: submittedBrandIds.length > 0 ? submittedBrandIds : undefined,
    status: submittedStatus,
    showTestStore: submittedShowTestStore,
    displayLimit,
    initialBatchSize: INITIAL_VISIBLE_STORE_ROWS,
    /** 仅关联合法视口就绪；列表是否非空由 useLoadingPage 内 shouldLoadInitial 判断（勿在此读 hotelListItems，避免 TDZ） */
    enableInitialBatch: listViewportReady,
  });

  const layoutMeasureRefs = useMemo(
    () => ({
      containerRef: layoutDebugContainerRef,
      leftInnerRef: layoutDebugLeftInnerRef,
      statCardsRef: layoutDebugStatRef,
      searchWrapRef: layoutDebugSearchRef,
      listHostRef: layoutDebugListHostRef,
      listScrollRef: scrollContainerRef,
      aiWrapRef: layoutDebugAiWrapRef,
    }),
    // ref 对象身份稳定；scrollContainerRef 来自 useLoadingPage 内 useRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useStoreRevenueMonitorLayoutDebugSampling(
    shouldShowDebugFloat(),
    layoutMeasureRefs,
  );

  /** 门店下拉选项：从 pms_hotel_cache 拉取；若选了大区则用第一个 regionId 过滤（接口仅支持单 regionId） */
  const singleRegionIdForOptions =
    regionIds.length > 0 ? regionIds[0] : undefined;
  const { hotels: allHotelsFromCache } = useHotelOptions({
    regionId: singleRegionIdForOptions
      ? Number(singleRegionIdForOptions)
      : undefined,
    pageSize: 2000,
    showTestStore,
  });

  /** 延后大数组更新，避免 2000 条酒店过滤/排序阻塞主线程导致菜单点击无响应 */
  const deferredAllHotels = useDeferredValue(allHotelsFromCache);

  /** 根据当前省/市/品牌/状态过滤后的酒店下拉选项，并转为 { value, label }，按名称排序 */
  const filteredHotelOptions = useMemo(() => {
    const provinceIdsForOptions = normalizeToNumberArray(
      formValues?.provinceId,
    );
    const cityIdsForOptions = normalizeToNumberArray(formValues?.cityId);
    const brandIdsForOptions = normalizeToNumberArray(formValues?.brandIds);
    const statusArrForOptions = normalizeToNumberArray(formValues?.status);
    const statusForOptions =
      statusArrForOptions.length > 0 ? statusArrForOptions[0] : undefined;

    const list = Array.isArray(deferredAllHotels) ? deferredAllHotels : [];
    const filtered = list.filter((h: any) => {
      if (!showTestStore && h.id === 1) return false;
      if (provinceIdsForOptions.length > 0) {
        const hp =
          typeof h.province === "number" ? h.province : Number(h.province);
        if (Number.isNaN(hp) || !provinceIdsForOptions.includes(hp))
          return false;
      }
      if (cityIdsForOptions.length > 0) {
        const hc = typeof h.city === "number" ? h.city : Number(h.city);
        if (Number.isNaN(hc) || !cityIdsForOptions.includes(hc)) return false;
      }
      if (brandIdsForOptions.length > 0) {
        const hb =
          typeof h.brandId === "number" ? h.brandId : Number(h.brandId);
        if (Number.isNaN(hb) || !brandIdsForOptions.includes(hb)) return false;
      }
      if (statusForOptions !== undefined) {
        const hs = typeof h.status === "number" ? h.status : Number(h.status);
        if (Number.isNaN(hs) || hs !== statusForOptions) return false;
      }
      return true;
    });

    return filtered
      .filter(
        (h: any) =>
          h?.id !== undefined && h?.id !== null && (h.name || h.fullName),
      )
      .map((h: any) => {
        const name = h.name || h.fullName || `门店 ${h.id}`;
        return { value: h.id, label: name };
      })
      .sort((a: any, b: any) =>
        String(a.label).localeCompare(String(b.label), "zh-CN"),
      );
  }, [
    deferredAllHotels,
    formValues?.provinceId,
    formValues?.cityId,
    formValues?.brandIds,
    formValues?.status,
    showTestStore,
  ]);

  // ---------------------------------------------------------------------------
  // 搜索面板配置
  // ---------------------------------------------------------------------------
  /** 搜索表单项（大区/省/市/品牌/门店/状态等），依赖上述枚举与 filteredHotelOptions */
  const searchItems = useMemo(() => {
    return createSearchItems({
      regionOptions,
      provinceOptions,
      cityOptions,
      brandOptions,
      hotelOptions: filteredHotelOptions,
    });
  }, [
    regionOptions,
    provinceOptions,
    cityOptions,
    brandOptions,
    filteredHotelOptions,
  ]);

  /** 搜索区按钮（搜索/重置等），图标由 getIcon 从字符串转为 React 节点 */
  const searchButtons = useMemo(() => {
    const buttonConfigs = createSearchButtons({
      handleSearchClick,
      handleReset,
    });
    return buttonConfigs.map(
      (config: SearchButtonConfig): ButtonConfig => ({
        key: config.key,
        icon: getIcon(config.iconName),
        text: config.text,
        type: config.type === "primary" ? "primary" : undefined,
        onClick: config.onClick,
        group: config.group as "primary" | "secondary" | "tertiary" | undefined,
      }),
    );
  }, [handleSearchClick, handleReset]);

  // ---------------------------------------------------------------------------
  // 与 AI 面板联动的点击回调（统计卡片 / 门店标题 / 组件标题）
  // 使用 useRef + useCallback([]) 保证引用稳定，减少子组件重渲染
  // ---------------------------------------------------------------------------
  const handleCardClick = useCallback(handleStatCardClickFromComponent, [
    handleStatCardClickFromComponent,
  ]);

  const handleStoreTitleClickFromComponentRef = useRef(
    handleStoreTitleClickFromComponent,
  );
  handleStoreTitleClickFromComponentRef.current =
    handleStoreTitleClickFromComponent;
  const handleStoreTitleClick = useCallback(
    (params: { id: string; name: string }) => {
      currentStoreIdForAiRef.current = params.id || "";
      setCurrentStoreIdForAi(params.id || "");
      setCurrentStoreNameForAi(params.name || "");
      handleStoreTitleClickFromComponentRef.current(params);
    },
    [],
  );

  const handleComponentTitleClickFromComponentRef = useRef(
    handleComponentTitleClickFromComponent,
  );
  handleComponentTitleClickFromComponentRef.current =
    handleComponentTitleClickFromComponent;
  /**
   * 图表/表格等组件标题点击：先交给 useAiAssistant，再转成标准消息发给 CompsAi.receiveMessage，
   * 由右侧 AI 面板发起分析（非受控模式，loading 与展示由 CompsAi 内部维护）。
   * 当与上一个组件的 storeId 或 componentType 不同时，先清理 CompsAi 中的选择状态，再传递当前选择显示。
   */
  const handleComponentTitleClick = useCallback(
    (params: {
      storeId: number;
      storeName?: string;
      componentType: string;
      componentData: any;
      storeData?: any;
    }) => {
      handleComponentTitleClickFromComponentRef.current(params);
        try {
        if (params.storeData != null) {
          lastStoreDataRef.current = {
            storeId: params.storeId,
            storeName: params.storeName ?? "",
            storeData: params.storeData,
          };
        }
        if (params.storeName != null && String(params.storeName).trim()) {
          setCurrentStoreNameForAi(String(params.storeName).trim());
        }
        const prev = lastComponentContextRef.current;
        const currentKey = `${params.storeId}:${params.componentType}`;
        const prevKey = prev
          ? `${prev.storeId}:${prev.componentType}`
          : null;

        // 与上一个组件名称、hotelId 等条件不同时，先清理 CompsAi 中的选择
        if (prevKey !== null && prevKey !== currentKey) {
          compsAiRef.current?.clearSelection?.();
        }
        lastComponentContextRef.current = {
          storeId: params.storeId,
          componentType: params.componentType,
        };

        const standardMessage = convertStoreComponentClickToStandard(
          {
            storeId: String(params.storeId),
            componentType: params.componentType as any,
            data: params.componentData,
            metadata: {
              label: getComponentLabel(params.componentType),
              storeName: params.storeName,
            },
            clearPrevious: true,
          } as any,
          "store-revenue-monitor",
        );
        const p = compsAiRef.current?.receiveMessage(standardMessage);
        p?.catch(() => {});
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("发送到AI失败：", e);
      }
    },
    [],
  );

  /** AI 助手列表「xxx 分析」单击：优先用上次点击组件的门店数据，否则用当前选中门店的缓存数据；出租率等运营分析若为空则用前端业务报告补拉 */
  const handleComponentAnalyze = useCallback(async (componentType: string) => {
    let storeId: number;
    let storeName: string;
    let storeData: any;
    const last = lastStoreDataRef.current;
    if (last?.storeData) {
      storeId = last.storeId;
      storeName = last.storeName;
      storeData = last.storeData;
    } else {
      const storeIdStr = currentStoreIdForAiRef.current;
      const cached = storeIdStr ? storeDataCacheRef.current[storeIdStr] : undefined;
      if (!cached?.storeData) {
        message.warning("请先点击某个门店标题或该门店下的组件，再进行列表分析");
        return;
      }
      storeId = cached.storeId;
      storeName = cached.storeName;
      storeData = cached.storeData;
    }
    try {
      // 提交 AI 分析时若缓存/合并后的 storeData 某 slice 为空则补拉，避免 AI 报「无数据」：
      // - 运营分析 7 类：后端 cache 不返，空则用 getBusinessReportDailyWithCache + buildOperationAnalysisFromList 补
      // - room-status-recently：空则 getRoomStatusRecentlyWithCache 补
      // - price-alert：暂无数据源，不补拉
      // - order-traffic / room-status / channel-price 等：后端或直调已有数据，一般不空
      let dataToUse = storeData;
      if (OPERATION_ANALYSIS_TYPES.includes(componentType)) {
        const fieldMap: Record<string, string> = {
          "occupancy-analysis": "occupancyAnalysis",
          "room-health": "roomHealth",
          "customer-source": "customerSource",
          "discount-analysis": "discountAnalysis",
          "channel-analysis": "channelAnalysis",
          "channel-info": "channelInfo",
          "operation-efficiency": "operationEfficiency",
        };
        const dataKey = fieldMap[componentType];
        const storeDataKey = componentType === "channel-info" ? "channelInfoChart" : dataKey;
        const currentSlice = storeData?.[storeDataKey];
        const isEmpty =
          currentSlice == null ||
          (currentSlice?.chartData != null &&
            (!Array.isArray((currentSlice.chartData as any)?.categories) ||
              (currentSlice.chartData as any).categories.length === 0));
        if (isEmpty) {
          try {
            const list = await getBusinessReportDailyWithCache(storeId);
            const built = buildOperationAnalysisFromList(list, "日度") as any;
            const value = componentType === "channel-info" ? built.channelInfo : built[dataKey];
            dataToUse = { ...storeData, [storeDataKey]: value };
          } catch (e) {
            logger.warn("补拉运营分析数据失败，使用原 storeData", { componentType, storeId, e });
          }
        }
      } else if (componentType === "room-status-recently") {
        const base = storeData?.roomStatusRecently?.baseChartData;
        const recentEmpty =
          base == null ||
          !Array.isArray((base as any)?.categories) ||
          (base as any).categories.length === 0;
        if (recentEmpty) {
          try {
            const baseChartData = await getRoomStatusRecentlyWithCache(storeId);
            dataToUse = { ...storeData, roomStatusRecently: { baseChartData } };
          } catch (e) {
            logger.warn("补拉远期房态数据失败，使用原 storeData", { storeId, e });
          }
        }
      }
      const componentData = buildComponentDataPackage(storeId, componentType, dataToUse);
      const standardMessage = convertStoreComponentClickToStandard(
        {
          storeId: String(storeId),
          componentType: componentType as any,
          data: componentData,
          metadata: {
            label: getComponentLabel(componentType),
            storeName,
          },
          clearPrevious: true,
        } as any,
        "store-revenue-monitor",
      );
      compsAiRef.current?.receiveMessage(standardMessage)?.catch(() => {});
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("列表分析发送到AI失败：", e);
    }
  }, []);

  /** 刷新单店：通过 ref 调用 useLoadingPage 的 refreshStore，保证回调引用稳定 */
  const refreshStoreRef = useRef(refreshStore);
  refreshStoreRef.current = refreshStore;
  const handleRefresh = useCallback((itemId: string | number) => {
    logger.debug(`刷新门店: ${itemId}（只影响该门店）`);
    refreshStoreRef.current?.(itemId);
  }, []);

  const loadStoreRef = useRef(loadStore);
  loadStoreRef.current = loadStore;
  const loadNextBatchRef = useRef(loadNextBatch);
  loadNextBatchRef.current = loadNextBatch;

  // ---------------------------------------------------------------------------
  // 门店可见性与自动刷新
  // ---------------------------------------------------------------------------
  const itemsToRender = useMemo(
    () => hotelListItems.slice(0, Math.min(displayLimit, hotelListItems.length)),
    [hotelListItems, displayLimit],
  );

  const hotelListItemsLengthRef = useRef(0);
  useEffect(() => {
    hotelListItemsLengthRef.current = hotelListItems.length;
  }, [hotelListItems.length]);

  const itemsToRenderRef = useRef(itemsToRender);
  useEffect(() => {
    itemsToRenderRef.current = itemsToRender;
  }, [itemsToRender]);

  /**
   * 筛选条件变化时重置为「只展示 3 行」；有数据且列表未在加载时打开首屏批量加载开关。
   */
  const prevListFilterKeyForLayoutRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (hotelListItems.length === 0) {
      setListViewportReady(false);
      prevListFilterKeyForLayoutRef.current = listFilterKey;
      return;
    }
    if (hotelsLoading) {
      return;
    }

    if (prevListFilterKeyForLayoutRef.current !== listFilterKey) {
      setDisplayLimit(INITIAL_VISIBLE_STORE_ROWS);
      prevListFilterKeyForLayoutRef.current = listFilterKey;
    }

    setListViewportReady(true);
  }, [hotelListItems.length, hotelsLoading, listFilterKey]);

  const handleListScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || itemsToRender.length >= hotelListItems.length) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (
      scrollTop + clientHeight >=
      scrollHeight - LIST_SCROLL_BOTTOM_THRESHOLD_PX
    ) {
      setDisplayLimit((prev) =>
        Math.min(prev + 1, hotelListItems.length),
      );
    }
  }, [scrollContainerRef, itemsToRender.length, hotelListItems.length]);

  /** displayLimit 或列表变化时，尝试加载当前展示范围内尚未拉数的门店 */
  useEffect(() => {
    if (!listViewportReady || hotelsLoading) return;
    loadNextBatchRef.current?.(false);
  }, [
    displayLimit,
    listViewportReady,
    hotelsLoading,
    hotelListItems.length,
    listFilterKey,
  ]);

  /** 当前列表中的门店 ID 列表（仅已渲染的），供可见性管理与刷新时间检查使用 */
  const storeIds = useMemo(
    () => itemsToRender.map((item) => item.id),
    [itemsToRender],
  );
  /** IntersectionObserver：注册每个门店 DOM，得到当前可见门店 ID 集合，用于按需/定时刷新 */
  const { registerStoreElement, visibleStoreIds } = useStoreVisibilityManager({
    storeIds,
    rootMargin: "100px",
    threshold: 0.1,
    onStoreVisible: (storeId) => {
      logger.debug(`门店 ${storeId} 进入可见范围，检查刷新时间`);
    },
  });

  /** 各门店内部刷新函数的注册表；自动刷新时通过 triggerRefresh(storeId) 调用对应门店的刷新 */
  const { registerRefresh, unregisterRefresh, triggerRefresh } =
    useStoreRefreshRegistry();

  /**
   * 按“最后刷新时间”做定期检查：仅对 visibleStoreIds 内的门店，若超过 5 分钟未刷新则触发一次刷新。
   * recordRefreshTime(storeId) 由 StoreDataLoader 在数据加载完成时调用。
   */
  const { recordRefreshTime } = useStoreRefreshTime({
    onRefresh: (storeId) => {
      logger.info(
        `门店 ${storeId} 超过5分钟未刷新，触发自动刷新（从后端重新取数）`,
      );
      triggerRefresh(storeId);
    },
    refreshInterval: 5 * 60 * 1000,
    visibleStoreIds,
    enablePeriodicCheck: true,
    checkInterval: 60 * 1000, // 60 秒检查一次，减轻 CPU 与请求压力
  });

  /**
   * 若仍停留在列表底部，单行加载完成后再扩 1 行（由 displayLimit 的 effect 触发 loadNextBatch）。
   */
  const maybeExpandListWhileAtBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const len = hotelListItemsLengthRef.current;
    if (len === 0) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (
      scrollTop + clientHeight <
      scrollHeight - LIST_SCROLL_BOTTOM_THRESHOLD_PX
    ) {
      return;
    }
    setDisplayLimit((prev) => (prev >= len ? prev : prev + 1));
  }, [scrollContainerRef]);

  const handleDataLoaded = useCallback(
    (storeId: number | string) => {
      recordRefreshTime(storeId);
      const id =
        typeof storeId === "string"
          ? Number.parseInt(storeId, 10)
          : storeId;
      if (Number.isNaN(id)) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const row = itemsToRenderRef.current;
          const lastId = row[row.length - 1]?.id;
          if (lastId === undefined || lastId !== id) return;
          maybeExpandListWhileAtBottom();
        });
      });
    },
    [recordRefreshTime, maybeExpandListWhileAtBottom],
  );

  // ---------------------------------------------------------------------------
  // 统计卡片数据（与门店详情数据完全隔离）
  // ---------------------------------------------------------------------------
  /** 延后启用 useAutoMonitor，避免首屏主线程被 generateMockStoresData + analyzeAllDimensions 阻塞，导致主菜单点击无响应 */
  const [monitorReady, setMonitorReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMonitorReady(true), 500);
    return () => clearTimeout(t);
  }, []);

  /**
   * 统计卡片数据源：useAutoMonitor 定时拉取/生成汇总数据，仅用于 StatisticCards。
   * 不依赖 visibleStoreIds，不触发门店列表刷新；门店详情由 StoreDataLoader + useLoadingStoreData 独立管理。
   */
  const { dimensionResults } = useAutoMonitor({
    enabled: monitorReady,
    refreshInterval: 60 * 1000,
    onlyRefreshVisible: false,
  });

  // ---------------------------------------------------------------------------
  // 布局与渲染
  // ---------------------------------------------------------------------------
  return (
    <div
      ref={layoutDebugContainerRef}
      className="store-revenue-monitor-container"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
        width: "100%",
        margin: 0,
        padding: 0,
        overflow: "hidden",
      }}
    >
      {/* 主内容区 + 右侧 AI 面板：Splitter 分割；整体 minHeight:0 以贴合外层高度，避免右栏被内容撑出视口 */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Splitter
          style={{ flex: 1, minHeight: 0, height: "100%", width: "100%" }}
        >
          {/* 左侧：统计（自然高度）→ 搜索（内容高度）→ 列表（flex 占满剩余并滚动） */}
          <Splitter.Panel
            min="30%"
            className="store-revenue-monitor-left-splitter-panel"
            style={{ height: "100%", minHeight: 0, overflow: "hidden" }}
          >
            <div
              ref={layoutDebugLeftInnerRef}
              className="store-revenue-monitor-left-inner"
            >
          {/* 1. 统计卡片：默认内容高度，不参与压缩 */}
          <div ref={layoutDebugStatRef} style={{ flexShrink: 0 }}>
            <StatisticCards
              dimensionResults={dimensionResults}
              onCardClick={handleCardClick}
            />
          </div>

          {/* 2. 搜索面板：高度随表单内容 */}
          <div ref={layoutDebugSearchRef} style={{ flexShrink: 0, minWidth: 0 }}>
          <ContainerPanel
            title={SEARCH_PANEL_CONFIG.SEARCH_TITLE}
            buttons={searchButtons}
            height="auto"
          >
            <CompsSearch
              searchItems={searchItems}
              columns={4}
              onSearch={applySearchSubmit}
              onReset={handleReset}
              collapsible={SEARCH_PANEL_CONFIG.COLLAPSIBLE}
              defaultCollapsed={SEARCH_PANEL_CONFIG.DEFAULT_COLLAPSED}
              showButtons={false}
              formRef={(form) => {
                searchFormRef.current = form;
              }}
              onValuesChange={(changedValues, allValues) => {
                if (isUpdatingRef.current) return;
                setFormValues(allValues || {});

                /* 联动清空：上级变化时清空下级，避免选项不在新范围内；用 isUpdatingRef 防止 setFieldsValue 再次触发 onValuesChange 造成循环 */
                const clearAndResetFlag = (
                  fields: Record<string, undefined>,
                ) => {
                  if (!searchFormRef.current) return;
                  isUpdatingRef.current = true;
                  searchFormRef.current.setFieldsValue(fields);
                  setTimeout(() => {
                    isUpdatingRef.current = false;
                  }, 0);
                };

                if (changedValues.regionId !== undefined) {
                  clearAndResetFlag({
                    provinceId: undefined,
                    cityId: undefined,
                    hotelId: undefined,
                  });
                }
                if (changedValues.provinceId !== undefined) {
                  clearAndResetFlag({ cityId: undefined, hotelId: undefined });
                }
                if (
                  changedValues.cityId !== undefined ||
                  changedValues.brandIds !== undefined ||
                  changedValues.status !== undefined
                ) {
                  clearAndResetFlag({ hotelId: undefined });
                }
              }}
            />
          </ContainerPanel>
          </div>

          {/* 3. 门店列表：高度锁在左栏第三行网格内，仅本区域滚动 */}
          <div
            ref={layoutDebugListHostRef}
            className="store-revenue-monitor-list-host"
          >
            <div
              ref={scrollContainerRef}
              onScroll={handleListScroll}
              className="store-revenue-monitor-list-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
            {(() => {
              if (hotelsLoading && hotelListItems.length === 0) {
                return (
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    <Spin size="large" tip="加载酒店列表..." />
                  </div>
                );
              }
              if (hotelListItems.length === 0) {
                return (
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    <Empty description="暂无符合条件的酒店" />
                  </div>
                );
              }
              /* 只渲染前 displayLimit 行，滚动到底再加载更多，减轻 CPU */
              return itemsToRender.map((item) => {
                if (!item.dataLoaded) {
                  return (
                    <div key={item.id} data-hotel-id={item.id}>
                      <HotelPlaceholder
                        hotelId={item.id}
                        hotelName={item.name}
                        loading={item.loading}
                        onTitleClick={() =>
                          handleStoreTitleClick({
                            id: String(item.id),
                            name: item.name,
                          })
                        }
                        onLoad={() => loadStore(item.id)}
                        onRefresh={() => handleRefresh(item.id)}
                      />
                    </div>
                  );
                }
                /* 已加载：渲染 StoreDataLoader（内部 useLoadingStoreData + 刷新注册）；React.memo 按 storeId/refreshKey/storeName 决定是否重渲染 */
                const refreshKey = getStoreRefreshKey(item.id);
                return (
                  <div
                    key={item.id}
                    data-hotel-id={item.id}
                    ref={(node) => {
                      registerStoreElement(item.id, node);
                    }}
                  >
                    {item.dataLoaded ? (
                      <StoreDataLoader
                        key={item.id}
                        storeId={item.id}
                        storeName={item.name}
                        refreshKey={refreshKey}
                        onTitleClick={() =>
                          handleStoreTitleClick({
                            id: String(item.id),
                            name: item.name,
                          })
                        }
                        onRefresh={() => handleRefresh(item.id)}
                        onComponentTitleClick={handleComponentTitleClick}
                        onDataLoaded={handleDataLoaded}
                        onStoreDataReady={(data) => {
                          storeDataCacheRef.current[String(item.id)] = {
                            storeId: item.id,
                            storeName: item.name ?? "",
                            storeData: data,
                          };
                        }}
                        registerRefresh={registerRefresh}
                        unregisterRefresh={unregisterRefresh}
                      />
                    ) : (
                      <div style={{ padding: "20px", textAlign: "center" }}>
                        <Spin size="small" tip="加载中..." />
                      </div>
                    )}
                  </div>
                );
              });
            })()}
            {hotelListItems.length > 0 && (
              <div
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontSize: "12px",
                  color: "#666",
                }}
              >
                已加载 {loadedCount} / {totalCount} 家酒店
                {itemsToRender.length < hotelListItems.length &&
                  ` · 显示 ${itemsToRender.length}/${hotelListItems.length} 行（滚到底每次多加载一行）`}
              </div>
            )}
            </div>
          </div>
            </div>
          </Splitter.Panel>

          {/* 右侧：AI 分析面板；限制在 Splitter 栏高内，不随对话内容无限增高 */}
          <Splitter.Panel
            defaultSize={300}
            min="260px"
            collapsible
            className="store-revenue-monitor-right-splitter-panel"
            style={{ height: "100%", minHeight: 0, maxHeight: "100%", overflow: "hidden" }}
          >
            <div
              ref={layoutDebugAiWrapRef}
              style={{
                height: "100%",
                minHeight: 0,
                maxHeight: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
          <CompsAi
            ref={compsAiRef as any}
            {...({
              pageId: "store-revenue-monitor",
              microServiceId: "msCentral",
              apiBaseUrl: undefined,
              onClearAll: clearAll,
              height: "100%",
              skipPageConfigLoad: true,
              thinkingModes: createThinkingModes(),
              componentList: ALL_COMPONENT_LIST,
              onComponentAnalyze: handleComponentAnalyze,
              currentStoreName: currentStoreNameForAi,
              currentStoreId: currentStoreIdForAi,
            } as any)}
          />
            </div>
          </Splitter.Panel>
        </Splitter>
      </div>
    </div>
  );
};

export default StoreRevenueMonitor;
export { StoreRevenueMonitorContent };
